import { useNavigate } from "@tanstack/react-router";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { usePermissions } from "@/modules/auth/presentation/model/usePermissions";
import { Permissions } from "@/modules/roles/domain/contracts/permissions";
import { useCreateOvertimeRequestMutation } from "../api/queries";
import { OTRequestForm } from "../ui/OTRequestForm";
import type { OTRequestFormValues } from "../ui/OTRequestForm";
import { Button, toast } from "@devhop/ui";
import { ArrowLeft, HelpCircle } from "lucide-react";
import { OTRequestFormTour, useOTRequestFormTour } from "../tour/OTRequestFormTour";

const REQUESTS_LIST_SEARCH = {
  offset: 0,
  limit: 20,
  status: undefined as string | undefined,
  search: undefined as string | undefined,
};

export function OTRequestFormPage() {
  const nav = useNavigate();
  const { has } = usePermissions();
  const canSelectEmployee =
    has(Permissions.hr_overtime.approve) || has(Permissions.hr_overtime.list);
  const createMutation = useCreateOvertimeRequestMutation();
  const tour = useOTRequestFormTour();

  const handleBack = () => {
    nav({ to: "/app/hr/overtime/requests", search: REQUESTS_LIST_SEARCH });
  };

  const handleSubmit = (values: OTRequestFormValues) => {
    createMutation.mutate(
      {
        workDate: values.workDate,
        startTime: values.startTime,
        endTime: values.endTime,
        reason: values.reason.trim() || undefined,
        ...(canSelectEmployee && values.employeeId?.trim()
          ? { employeeId: values.employeeId.trim() }
          : {}),
      },
      {
        onSuccess: () => {
          toast.success("ສົ່ງຄຳຂໍ OT ສຳເລັດ");
          nav({ to: "/app/hr/overtime/requests", search: REQUESTS_LIST_SEARCH });
        },
        onError: (err: { message?: string; status?: number }) => {
          const msg = err?.message ?? "";
          if (msg.includes("Duplicate") || (err as { status?: number })?.status === 409) {
            toast.error("ທ່ານມີຄຳຂໍ OT ສຳລັບວັນນີ້ແລ້ວ ບໍ່ສາມາດສົ່ງຊ້ຳໄດ້");
            return;
          }
          toast.error(msg || "ເກີດຂໍ້ຜິດພາດ");
        },
      },
    );
  };

  return (
    <>
      <Header />
      <Main>
        <OTRequestFormTour run={tour.run} onCallback={tour.handleJoyrideCallback} />
        <div className="mb-6 flex items-center justify-between gap-2" data-tourid="ot-request-form-back">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-semibold text-2xl">ຟອມຄຳຂໍ OT</h1>
              <Button
                variant="ghost"
                size="icon"
                className="size-7 rounded-full"
                onClick={tour.startTour}
                title="ເລີ່ມທົດສອບການນຳທາງ"
                aria-label="ເລີ່ມທົດສອບການນຳທາງ"
              >
                <HelpCircle className="size-5" />
              </Button>
            </div>
            <p className="text-muted-foreground text-sm">
              ສົ່ງຄຳຂໍ OT ໃໝ່
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={handleBack} className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            ກັບໄປລາຍການ
          </Button>
        </div>

        <div className="mx-auto w-full max-w-4xl space-y-6" data-tourid="ot-request-form-form">
          <OTRequestForm
            mode="create"
            canSelectEmployee={canSelectEmployee}
            onSubmit={handleSubmit}
            onCancel={handleBack}
            isSubmitting={createMutation.isPending}
          />
        </div>
      </Main>
    </>
  );
}
